import React from "react";
import CommonView, { CommonViewProps } from "../../../components/CommonView/CommonView";
import { MasterConfigs } from "../../../interfaces/entity";
import MasterComponent from "../../../components/Forms";
import { Button, Form, message, Segmented, Typography } from "antd";
import { useDispatch } from "react-redux";
import { postData } from "../../../actions/developer";
import { encryptData } from "../../../actions/crypto";
const { Title } = Typography;
const { LoadingOutlined } = require("@ant-design/icons");

export const languageMap = {
    "1": "abap",
    "43": "plaintext",
    "45": "bat",
    "46": "shell",
    "50": "c",
    "51": "csharp",
    "54": "cpp",
    "55": "clojure",
    "56": "coffeescript",
    "60": "go",
    "61": "haskell",
    "62": "java",
    "63": "javascript",
    "64": "julia",
    "65": "lua",
    "67": "pascal",
    "68": "php",
    "71": "python",
    "72": "ruby",
    "73": "powershell",
    "74": "typescript",
    "78": "kotlin",
    "79": "objective-c",
    "80": "r",
    "81": "scala",
    "82": "sql",
    "83": "swift",
    "84": "pgsql",
    "85": "perl",
    "87": "vb",
    "88": "groovy",
    "90": "dart",
    "92": "fsharp",
    "93": "rust"
};

const colors = {
    pending: "#FF9500",
    passed: "#009921",
    failed: "#FF0000",
    error: "#9F0000",
}

const getColor = (status: any) => {
    switch (true) {
        case status == 3:
            return {
                color: colors.passed,
                text: "Success",
            };
        case status == 4:
            return {
                color: colors.failed,
                text: "Failed",
            }
        case status > 4:
            return {
                color: colors.error,
                text: "Error",
            };
        default:
            return {
                color: colors.pending,
                text: "Pending",
            };
    }
}



const CodingQuestions = () => {
    const [refresh, setRefresh] = React.useState(false)
    const [selectedId, setSelectedId] = React.useState(null);
    const [language, setLanguage] = React.useState(null);
    const [value, setValue] = React.useState<any>({});
    const [codeOutput, setCodeOutput] = React.useState([]);
    const [loadingOutput, setLoadingOutput] = React.useState(false);
    const [outPutWindow, setOutPutWindow] = React.useState({});

    const dispatch = useDispatch();

    const onRun = async () => {
        try {
            if (!value) {
                message.error("Please fill in the question details first.");
                return;
            }

            let item = value;

            // Validate required fields
            if (!item?.cases) {
                message.error("Please add at least one test case before running the code.");
                return;
            }

            if (!Array.isArray(item.cases)) {
                message.error("Test cases must be in array format. Please check the form.");
                return;
            }

            if (item.cases.length === 0) {
                message.error("Please add at least one test case before running the code.");
                return;
            }

            // Validate each test case has required fields
            const invalidCases = item.cases.filter((tc) => {
                const hasInput = tc?.input !== undefined && tc?.input !== null && tc?.input !== '';
                const hasOutput = tc?.output !== undefined && tc?.output !== null && tc?.output !== '';
                return !hasInput || !hasOutput;
            });

            if (invalidCases.length > 0) {
                message.error(`Please fill in both input and output for all test cases. ${invalidCases.length} case(s) are incomplete.`);
                return;
            }

            if (!item?.code || !item?.language) {
                message.error("Please provide code and select a language.");
                return;
            }

            setLoadingOutput(true);
            setCodeOutput([]);
            const submissions = item.cases.map(tc => ({
                language_id: item.language,
                source_code: item.code,
                stdin: tc.input || '',
                expected_output: tc.output || ''
            }));

            const response = await dispatch(postData('/code-run', { data: encryptData({ submissions }) }));

            if (response.code == 200) {
                setCodeOutput(response.data);
                if (response.data[0]?.compile_output || response.data[0]?.stderr) {
                    setOutPutWindow('Console');
                } else {
                    setOutPutWindow('Cases');
                }
            } else {
                message.error("Error executing code.");
            }
        } catch (error) {
            message.error("Error executing code.");
        } finally {
            setLoadingOutput(false);
        }
    }

    const config: MasterConfigs = {
        api: () => '/skills',
        modalTitle: () => 'Skills',
        onRefresh: () => {
            setRefresh(true)
        },
        getFormColumns: () => [
            {
                label: 'Skill Name',
                name: 'name',
                type: 'text',
                rules: [{ required: true, message: 'Please enter Skill name' }]
            },
            {
                label: 'Short Code',
                name: 'shortCode',
                type: 'text',
                rules: [{ required: true, message: 'Please enter Short Code' }]
            },
            {
                label: 'Description',
                name: 'description',
                type: 'textarea',
                span: 16,
            },
        ],
    }

    const McqQuestionConfig: MasterConfigs = {
        api: () => '/codding-questions',
        apiParams: { skills: selectedId },
        queryParams: { skills: selectedId },
        modalTitle: () => 'Coding Questions',
        isActionTab: true,
        onDataLoad: (data) => {
            // Set the value state when data is loaded for editing
            setValue(data);
            // Also set language if it exists
            if (data?.language) {
                setLanguage(data.language);
            }
        },
        handleValuesChange: (e, values) => {
            setValue(values)
        },
        getColumns: () => [
            {
                title: 'Question',
                dataIndex: 'question',
                key: 'question',
                ellipsis: true
            },
            {
                title: 'Cases',
                dataIndex: 'cases',
                key: 'cases',
                ellipsis: true,
                render: (cases) => {
                    return cases.map((item, index) => {
                        return <div key={index}>{String.fromCharCode(65 + index) + ") "}{item.input} ({item?.output})</div>
                    })
                }
            },
        ],
        getFormColumns: () => [
            {
                label: 'Question',
                name: 'question',
                type: 'textarea',
                span: 16,
                rules: [{ required: true, message: 'Please enter Question' }]
            },
            {
                label: 'Language',
                name: 'language',
                type: 'select',
                span: 8,
                isCombo: true,
                selectProps: {
                    onChange: (value) => {
                        setLanguage(value)
                    }
                },
                rules: [{ required: true, message: 'Please select language' }]
            },
            {
                label: 'Problem Statement',
                name: 'details',
                type: 'quill',
                span: 24,
                rules: [{ required: true, message: 'Please enter problem statement' }]
            },
            {
                type: 'custom',
                formItem: () => <Title level={5}>Code Editor</Title>,
                span: 24
            },
            {
                name: 'code',
                type: 'editor',
                span: 24,
                editorProps: {
                    height: 300,
                    language: language ? languageMap[language] : 'plaintext',
                }
            },
            {
                type: 'custom',
                formItem: () => <div className="flex justify-between items-center">
                    <Segmented
                        options={['Cases', 'Console']}
                        value={outPutWindow}
                        onChange={(value) => {
                            setOutPutWindow(value);
                        }}
                    />
                    <Button type="primary" className="float-rigth" onClick={() => onRun()} loading={loadingOutput}>Run Code</Button>
                </div>,
                span: 24
            },
            {
                type: 'custom',
                formItem: () => <>
                    <div className="pt-4 p-3 h-full overflow-auto bg-[#e6e9f3]">
                        {outPutWindow == 'Cases' && <div className="grid md:grid-cols-2 sm:grid-cols-1 gap-3">
                            {value?.cases?.map((ele, index) => <div className={`bg-white flex flex-rows justify-between items-center p-2 border border-[${getColor(codeOutput?.[index]?.status_id)?.color}] rounded-md px-4`} key={index}>
                                <div className="text-gray-800 font-semibold">
                                    {codeOutput?.[index]?.stdout ? codeOutput?.[index]?.stdout : `Case ${index + 1}`}
                                </div>
                                <div className="">
                                    {loadingOutput && <div className={`bg-[${getColor(codeOutput?.[index]?.status_id)?.color}] w-full h-8 justify-center flex items-center rounded-lg text-white bold px-8`}>
                                        <LoadingOutlined spin={loadingOutput} className="text-white" />
                                    </div>}
                                </div>
                            </div>)}
                        </div>}
                        {outPutWindow == 'Console' && <pre className="text-gray-800 whitespace-pre-wrap">
                            {loadingOutput ? <LoadingOutlined spin={loadingOutput} className="text-primary text-2xl" /> :
                                codeOutput?.[0]?.compile_output || codeOutput?.[0]?.stderr || codeOutput?.[0]?.stdout || "No Error"}</pre>}
                    </div>
                </>,
                span: 24
            },
            {
                type: 'custom',
                formItem: () => <Title level={5}>Cases</Title>,
                span: 24
            },
            {
                name: 'cases',
                type: 'form',
                span: 16,
                form: [{
                    label: 'Input',
                    name: 'input',
                    type: 'text',
                    rules: [{ required: true, message: 'Please enter input' }]
                },
                {
                    label: 'Output',
                    name: 'output',
                    type: 'text',
                    rules: [{ required: true, message: 'Please enter output' }]
                }],
            },
            {
                span: 8
            },
        ],
        actions: [{ actionType: 'Add', name: 'Add' }],
        combo: [
            {
                api: '/code-run/languages',
                name: "language",
                idKey: "id",
                render: (record: any) => record.name,
            }
        ]
    }


    const componentConfig: CommonViewProps = {
        config: config,
        listProps: {
            reload: refresh,
            onLoaded: () => {
                setRefresh(false)
            }
        },
        componentConfig: {
            title: "Coding Questions",
            subTitle: "Coding Questions",
            cardTitle: "Coding Questions",
            buttonName: "Add Question",
            emptyDescription: "No Coding Questions found",
            renderComponent: (record: any) => <MasterComponent config={McqQuestionConfig} />,
            renderListItem: (record: any) => record.name,
            selectedItemId: (id) => setSelectedId(id),
        }
    }

    return <>
        <CommonView componentConfig={componentConfig.componentConfig} config={componentConfig.config} />
    </>;
}

export default CodingQuestions;